<script setup lang="ts">
import { useIntervalFn, useWindowSize } from "@vueuse/core";
import { addMonths, isAfter, isBefore, setDay } from "date-fns";
import { twMerge } from "tailwind-merge";
import { computed, ref } from "vue";

import { useUser } from "@/composables/api/useUser";
import { rewards } from "@/rewards";

import CountdownItem from "./CountdownItem.vue";
import GiftsGrid from "./GiftsGrid.vue";
import GiftsSlider from "./GiftsSlider.vue";

const { width } = useWindowSize();
const { data: user } = useUser();

const currentRewards = computed(() => {
  if (!user.value?.current_mechanic) {
    return [];
  }
  const mechanic = user.value.current_mechanic;
  return rewards[mechanic];
});
const numberOfavailableGifts = computed(() => {
  const userPoints = user.value?.loyalty_points ?? 0;
  const alreadyWonDotations = user.value?.won_dotation_ids ?? [];

  if (!userPoints) {
    return 0;
  }

  return (
    currentRewards.value.filter((reward) => userPoints >= reward.points).length -
    alreadyWonDotations.length
  );
});

// Create date in France timezone
function getNow() {
  const now = new Date();
  // Ensure we get the date in France timezone (UTC+1/+2 depending on DST)
  return new Date(now.toLocaleString("en-US", { timeZone: "Europe/Paris" }));
}

const now = ref(getNow());

useIntervalFn(
  () => {
    now.value = getNow();
  },
  1000,
  { immediate: true, immediateCallback: true },
);

// Calculate the next draw date (first Monday of the month)
const nextDrawDate = computed(() => {
  const currentDate = now.value;
  let targetDate = new Date(currentDate);

  // Start with May 2025 if current date is before May 2025
  const startingMonth = 4; // May (0-indexed)
  const startingYear = 2025;
  const initialTarget = new Date(startingYear, startingMonth, 1);

  if (isBefore(currentDate, initialTarget)) {
    targetDate = initialTarget;
  }

  // Set to first day of current month
  targetDate.setDate(1);

  // Get the first Monday of the current month
  const firstMondayThisMonth = setDay(new Date(targetDate), 1); // 1 = Monday
  if (firstMondayThisMonth.getMonth() !== targetDate.getMonth()) {
    // If first Monday falls in previous month, add 7 days
    firstMondayThisMonth.setDate(firstMondayThisMonth.getDate() + 7);
  }
  firstMondayThisMonth.setHours(0, 0, 0, 0);

  // If we are the first Monday of the month and it's after 11h, go to the next month
  const isFirstMondayToday =
    currentDate.getFullYear() === firstMondayThisMonth.getFullYear() &&
    currentDate.getMonth() === firstMondayThisMonth.getMonth() &&
    currentDate.getDate() === firstMondayThisMonth.getDate();

  if (
    (isAfter(currentDate, firstMondayThisMonth) && !isFirstMondayToday) ||
    (isFirstMondayToday && currentDate.getHours() >= 11)
  ) {
    // Passer au mois suivant
    targetDate = addMonths(targetDate, 1);
  }

  // Get the first Monday of the target month
  const firstMonday = setDay(new Date(targetDate), 1); // 1 = Monday
  if (firstMonday.getMonth() !== targetDate.getMonth()) {
    firstMonday.setDate(firstMonday.getDate() + 7);
  }
  firstMonday.setHours(11, 0, 0, 0);

  return firstMonday;
});

// Calculate time duration until next draw
const duration = computed(() => {
  const diff = nextDrawDate.value.getTime() - now.value.getTime();
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  const totalSeconds = Math.floor(diff / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const totalHours = Math.floor(totalMinutes / 60);

  return {
    days: Math.floor(totalHours / 24),
    hours: totalHours % 24,
    minutes: totalMinutes % 60,
    seconds: totalSeconds % 60,
  };
});
</script>

<template>
  <div
    class="gifts-card flex w-full max-w-xs flex-col items-center rounded-[20px] bg-white py-5 lg:max-w-5xl lg:py-10 lg:shadow-[0px_0px_20px_0px_rgba(0,0,0,0.1)]"
  >
    <h3
      :class="
        twMerge(
          'font-brygada text-galbani px-5 text-center text-xl font-bold lg:mb-8 lg:text-5xl',
          user?.current_mechanic === 'lactel' && 'font-mitr text-lactel-blue',
        )
      "
    >
      {{ $t("gifts.title") }}
    </h3>

    <GiftsSlider v-if="width < 1024" />

    <GiftsGrid v-if="width >= 1024" />

    <div class="mt-8 flex flex-col gap-3 px-5">
      <p
        :class="
          twMerge(
            'text-center text-xl font-semibold text-black lg:text-[28px]',
            user?.current_mechanic === 'lactel' && 'text-lactel-blue',
          )
        "
      >
        {{ $t("gifts.available", { n: numberOfavailableGifts }) }}
      </p>

      <p
        :class="
          twMerge(
            'text-center text-base text-black lg:text-xl',
            user?.current_mechanic === 'lactel' && 'text-lactel-blue',
          )
        "
      >
        {{ $t("gifts.nextDraw") }}
      </p>
    </div>

    <div class="mt-4 flex items-center justify-center gap-2 lg:gap-4">
      <CountdownItem :value="duration.days ?? 0" :label="`jour${duration.days === 0 ? '' : 's'}`" />

      <span
        :class="
          twMerge(
            'text-galbani -mt-6 text-base font-medium lg:text-xl',
            user?.current_mechanic === 'lactel' && 'text-lactel-blue',
          )
        "
      >
        :
      </span>

      <CountdownItem
        :value="duration.hours ?? 0"
        :label="`heure${duration.hours === 0 ? '' : 's'}`"
      />

      <span
        :class="
          twMerge(
            'text-galbani -mt-6 text-base font-medium lg:text-xl',
            user?.current_mechanic === 'lactel' && 'text-lactel-blue',
          )
        "
      >
        :
      </span>

      <CountdownItem
        :value="duration.minutes ?? 0"
        :label="`minute${duration.minutes === 0 ? '' : 's'}`"
      />
    </div>
  </div>
</template>
